/**
 * @fileoverview MultipassListRow — single row inside a Multipass list-table
 * (e.g. Authorized applications, Tokens, Connected accounts). Two-column
 * layout: a left "client" cell composed of an optional image + a text
 * stack (large name + muted secondary line, often containing an
 * `OrganizationTag`), and a right actions slot (typically an "Actions"
 * dropdown button).
 *
 * Foundry mapping: `multipass-internal__row` containing a
 * `multipass-internal__client` (`__image-container` + `__image` +
 * `__text`) and a trailing actions slot (`multipass-internal__actions`).
 * Rows stack inside a `PanelCard` configured as `multipass-internal__table`.
 */

import type { ReactElement, ReactNode } from "react";

interface MultipassListRowProps {
  /** Optional logo / avatar image rendered on the left. */
  imageUrl?: string;
  /** Primary name (rendered as bold, larger text). */
  name: ReactNode;
  /** Optional muted secondary line beneath the name (e.g. "Managed by …"). */
  secondary?: ReactNode;
  /** Right-side actions slot — typically an "Actions" Button. */
  actions?: ReactNode;
  className?: string;
}

export function MultipassListRow({
  imageUrl,
  name,
  secondary,
  actions,
  className,
}: MultipassListRowProps): ReactElement {
  return (
    <div data-testid="multipass-list-row" className={["bp-mp-list-row", className].filter(Boolean).join(" ")}>
      <div className="bp-mp-list-row-client">
        {imageUrl && (
          <div className="bp-mp-list-row-image-container">
            <img className="bp-mp-list-row-image" src={imageUrl} alt="" />
          </div>
        )}
        <div className="bp-mp-list-row-text">
          <div className="bp6-text-overflow-ellipsis bp6-text-large bp-mp-list-row-name">
            <strong>{name}</strong>
          </div>
          {secondary !== undefined && (
            <div className="bp6-text-muted bp-mp-list-row-secondary">{secondary}</div>
          )}
        </div>
      </div>
      {actions !== undefined && (
        <div className="bp-mp-list-row-actions">{actions}</div>
      )}
    </div>
  );
}
